import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/Textarea";
import LanguageDropdown from "../ui/LanguageDropdown";
import CountryDropdown from "../ui/CountryDropdown";
import { CommonQuestion, CommonQuestionCreateDto, CommonQuestionUpdateDto } from "../../modules/shared/model/common-questions.interfaces";
import { CommonQuestionCategoryEnum } from "../../modules/shared/model/common-questions.enum";
import { Language } from "../../modules/shared/model/LanguageInterfaces";
import { useAppSelector } from "../../redux/store";

interface FaqModalProps {
  faq?: CommonQuestion | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CommonQuestionCreateDto | CommonQuestionUpdateDto) => void;
  loading?: boolean;
}

const FaqModal: React.FC<FaqModalProps> = ({ 
  faq = null, 
  isOpen, 
  onClose, 
  onSubmit, 
  loading = false 
}) => {
  // Get countries from Redux store to help with country selection
  const { countries } = useAppSelector((state: any) => state.admin || { countries: [] });
  
  const [formData, setFormData] = useState<CommonQuestionCreateDto>({
    question: "",
    answer: "",
    likes: 0,
    views: 0,
    category: null,
    languages: [],
    countries: []
  });

  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<{id: string; name: string; code: string} | null>(null);
  const [selectedCountries, setSelectedCountries] = useState<{id: string; name: string; code: string}[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize form data when faq changes or modal opens
  useEffect(() => {
    // Only initialize when modal opens or FAQ changes, not when countries list updates during search
    if (faq && isOpen && !isInitialized) {
      setFormData({
        question: faq.question || "",
        answer: faq.answer || "",
        likes: faq.likes || 0,
        views: faq.views || 0,
        category: typeof faq.category === "object" ? (faq.category?.id ? { id: parseInt(faq.category.id.toString()) } : null) : (typeof faq.category === "number" ? { id: faq.category } : null),
        languages: faq.languages || [],
        countries: faq.countries || []
      });
      
      // Convert language objects to Language interface
      if (faq.languages) {
        const languages: Language[] = faq.languages.map(lang => ({
          id: lang.id,
          name: lang.name, // Assuming name is same as id for now
          initial: lang.id,
          direction: { id: "1", name: "LTR" },
          isDefault: false,
          isTranslationEnable: false,
          isAutomaticSync: false,
          isNotify: false,
          autoTranslate: { id: "1" },
          translationMemory: { id: "1" },
          categories: []
        }));
        setSelectedLanguages(languages);
      }
      
      // Process country data with full country information from Redux
      if (faq.countries && faq.countries.length > 0) {
        const processedCountries = faq.countries.map(country => {
          // Try to find the full country data from the Redux store
          const fullCountryData = countries.find((c: any) => c.id === country.id);
          
          return {
            id: country.id,
            name: country.name || fullCountryData?.name || (country as any).countryName || `Country ${country.id}`,
            code: country.code || fullCountryData?.code || (country as any).countryCode || country.id
          };
        });
        setSelectedCountries(processedCountries);
        setSelectedCountry(processedCountries[0]);
      } else {
        setSelectedCountries([]);
        setSelectedCountry(null);
      }
      setIsInitialized(true);
    } else if (!faq && isOpen && !isInitialized) {
      // Creating new FAQ
      setFormData({
        question: "",
        answer: "",
        likes: 0,
        views: 0,
        category: null,
        languages: [],
        countries: []
      });
      setSelectedLanguages([]);
      setSelectedCountry(null);
      setSelectedCountries([]);
      setIsInitialized(true);
    } else if (!isOpen) {
      // Modal closed - reset initialization flag
      setIsInitialized(false);
    }
    if (isOpen && !isInitialized) {
      setErrors({});
    }
  }, [faq, isOpen, isInitialized, countries]);

  const handleInputChange = useCallback((field: keyof CommonQuestionCreateDto, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  }, [errors]);

  const handleLanguageSelect = useCallback((languages: Language[]) => {
    setSelectedLanguages(languages);
    setFormData(prev => ({
      ...prev,
      languages: languages.map(lang => ({ id: lang.id }))
    }));
  }, []);

  const handleCountrySelect = useCallback((country: {id: string; name: string; code: string} | null) => {
    setSelectedCountry(country);
    setFormData(prev => ({
      ...prev,
      countries: country ? [{ id: country.id }] : []
    }));
  }, []);

  const handleCountriesSelect = useCallback((countries: {id: string; name: string; code: string}[]) => {
    setSelectedCountries(countries);
    setFormData(prev => ({
      ...prev,
      countries: countries.map(country => ({ id: country.id }))
    }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.question?.trim()) {
      newErrors.question = "Question is required";
    }

    if (!formData.answer?.trim()) {
      newErrors.answer = "Answer is required";
    }

    if (!formData.category) {
      newErrors.category = "Category is required";
    }

    if (!formData.languages || formData.languages.length === 0) {
      newErrors.languages = "At least one language is required";
    }

    if (!formData.countries || formData.countries.length === 0) {
      newErrors.countries = "Country is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Submit with all selected countries in a single FAQ record
    onSubmit(formData);
  }, [validateForm, onSubmit, formData]);

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  // Memoize the selectedLanguages and selectedCountry to prevent infinite re-renders
  const memoizedSelectedLanguages = useMemo(() => selectedLanguages, [selectedLanguages]);
  const memoizedSelectedCountry = useMemo(() => selectedCountry, [selectedCountry]);
  const memoizedSelectedCountries = useMemo(() => selectedCountries, [selectedCountries]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b sticky top-0 bg-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {faq ? "Edit FAQ" : "Add New FAQ"}
              </h2>
              <button
                onClick={handleClose}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-full disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6" style={{ textAlign: "left" }}>
            {/* Question */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question *
              </label>
              <Input
                type="text"
                value={formData.question || ""}
                onChange={(e) => handleInputChange("question", e.target.value)}
                placeholder="Enter FAQ question"
                className={errors.question ? "border-red-500" : ""}
                disabled={loading}
              />
              {errors.question && (
                <p className="text-red-500 text-sm mt-1">{errors.question}</p>
              )}
            </div>

            {/* Answer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Answer *
              </label>
              <Textarea
                value={formData.answer || ""}
                onChange={(e) => handleInputChange("answer", e.target.value)}
                placeholder="Enter FAQ answer"
                rows={6}
                className={errors.answer ? "border-red-500" : ""}
                disabled={loading}
              />
              {errors.answer && (
                <p className="text-red-500 text-sm mt-1">{errors.answer}</p>
              )}
            </div>

            {/* Category and Languages */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={formData.category?.id || ""}
                  onChange={(e) => handleInputChange("category", e.target.value ? { id: parseInt(e.target.value) } : null)}
                  className={`w-full px-3 py-2 border rounded-lg ${errors.category ? "border-red-500" : ""}`}
                  disabled={loading}
                >
                  <option value="">Select Category</option>
                  {Object.entries(CommonQuestionCategoryEnum)
                    .filter(([key, value]) => typeof value === "number")
                    .map(([key, value]) => (
                      <option key={value} value={value}>
                        {key}
                      </option>
                    ))}
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Languages *
                </label>
                <LanguageDropdown
                  selectedLanguages={memoizedSelectedLanguages}
                  onLanguagesSelect={handleLanguageSelect}
                  onLanguageSelect={() => {}} // Required prop but not used in multiple mode
                  placeholder="Select languages"
                  multiple={true}
                  disabled={loading}
                />
                {errors.languages && (
                  <p className="text-red-500 text-sm mt-1">{errors.languages}</p>
                )}
              </div>
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {selectedCountries.length > 1 ? "Countries" : "Country"} *
              </label>
              <CountryDropdown
                selectedCountry={memoizedSelectedCountry}
                selectedCountries={memoizedSelectedCountries}
                onCountrySelect={handleCountrySelect}
                onCountriesSelect={handleCountriesSelect}
                placeholder="Select country/countries"
                multiple={true}
                disabled={loading}
              />
              {errors.countries && (
                <p className="text-red-500 text-sm mt-1">{errors.countries}</p>
              )}
              {selectedCountries.length > 1 && (
                <p className="text-blue-600 text-sm mt-1">
                  {selectedCountries.length} countries selected. This FAQ will be available in all selected countries.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="min-w-[120px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {faq ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  faq ? "Save Changes" : "Add FAQ"
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default FaqModal;
